import { memo, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { ScrollView, View, StyleSheet } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import {
  BottomSheetContainer,
  Colors,
  createUseBottomSheetModal,
  Flex,
  Typography,
} from '@banhala/ui-kit';
import { useSafeTimeout } from 'shared/src/hooks';

const ITEM_HEIGHT = 44;
const VISIBLE_ITEMS = 5;
const PICKER_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS;
const NONE_LABEL = '선택안함';

type PickerOption = {
  label: string;
  value: number;
};

type PickerResult = { selected: number | null };
type PickerParam = {
  title: string;
  options: PickerOption[];
  current: number | null;
  defaultValue?: number;
};

type WheelItem = {
  label: string;
  value: number | null;
};

const PickerGradientOverlay = memo(() => (
  <View style={StyleSheet.absoluteFill} pointerEvents="none">
    <Svg width="100%" height={PICKER_HEIGHT} pointerEvents="none">
      <Defs>
        <LinearGradient id="pickerFade" x1="0" y1="0" x2="0" y2="1">
          <Stop offset="0" stopColor={Colors.bg_always_white} stopOpacity="1" />
          <Stop offset="0.5" stopColor={Colors.bg_always_white} stopOpacity="0" />
          <Stop offset="1" stopColor={Colors.bg_always_white} stopOpacity="1" />
        </LinearGradient>
      </Defs>
      <Rect x="0" y="0" width="100%" height={PICKER_HEIGHT} fill="url(#pickerFade)" />
    </Svg>
  </View>
));

const PickerSelectedHighlight = memo(() => (
  <View style={StyleSheet.absoluteFill} pointerEvents="none">
    <View style={styles.selectedHighlight} />
  </View>
));

const findItemIndex = (items: WheelItem[], value: number | null | undefined) => {
  if (value == null) return -1;
  return items.findIndex(item => item.value === value);
};

type PickerItemProps = {
  label: string;
  index: number;
  isSelected: boolean;
  onPress: (index: number) => void;
};

const PickerItem = memo(
  ({ label, index, isSelected, onPress }: PickerItemProps) => (
    <Flex
      height={ITEM_HEIGHT}
      alignItems="center"
      justifyContent="center"
      onPress={() => onPress(index)}
    >
      <Typography
        type={isSelected ? 'subtitle2' : 'body2'}
        color="content_primary"
      >
        {label}
      </Typography>
    </Flex>
  ),
);

type SizePickerContentProps = {
  confirm: (result: PickerResult) => void;
  param: PickerParam;
};

export const SizePickerContent = ({
  confirm,
  param,
}: SizePickerContentProps) => {
  const { title, options, current, defaultValue } = param;

  const items = useMemo<WheelItem[]>(
    () => [
      { label: NONE_LABEL, value: null },
      ...options.map(o => ({ label: o.label, value: o.value })),
    ],
    [options],
  );

  const safeInitialIndex = useMemo(() => {
    const currentIdx = findItemIndex(items, current);
    if (currentIdx >= 0) return currentIdx;
    const defaultIdx = findItemIndex(items, defaultValue);
    if (defaultIdx >= 0) return defaultIdx;
    return 0;
  }, [items, current, defaultValue]);

  const [selectedIndex, setSelectedIndex] = useState(safeInitialIndex);
  const scrollViewRef = useRef<ScrollView>(null);
  const initialScrollDoneRef = useRef(false);
  const scrollIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setSafeTimeout = useSafeTimeout();

  useEffect(() => {
    setSelectedIndex(safeInitialIndex);
    initialScrollDoneRef.current = false;
  }, [safeInitialIndex]);

  const handleContentSizeChange = useCallback(() => {
    if (initialScrollDoneRef.current) return;
    scrollViewRef.current?.scrollTo({
      y: safeInitialIndex * ITEM_HEIGHT,
      animated: false,
    });
    initialScrollDoneRef.current = true;
  }, [safeInitialIndex]);

  const handleScroll = useCallback(
    (event: { nativeEvent: { contentOffset: { y: number } } }) => {
      const rawOffset = event.nativeEvent.contentOffset.y;
      const index = Math.max(
        0,
        Math.min(Math.round(rawOffset / ITEM_HEIGHT), items.length - 1),
      );
      setSelectedIndex(index);

      if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
      scrollIdleTimerRef.current = setSafeTimeout(() => {
        const targetOffset = index * ITEM_HEIGHT;
        if (Math.abs(rawOffset - targetOffset) > 0.5) {
          scrollViewRef.current?.scrollTo({
            y: targetOffset,
            animated: true,
          });
        }
      }, 150);
    },
    [items.length, setSafeTimeout],
  );

  const handleItemPress = useCallback((index: number) => {
    setSelectedIndex(index);
    scrollViewRef.current?.scrollTo({
      y: index * ITEM_HEIGHT,
      animated: true,
    });
  }, []);

  const handleConfirm = useCallback(() => {
    const selected = items[selectedIndex];
    confirm({ selected: selected?.value ?? null });
  }, [confirm, items, selectedIndex]);

  return (
    <BottomSheetContainer
      showHandleBar
      hideBorder
      title={title}
      bottomDocked={{
        paired: false,
        button: { title: '선택하기', onPress: handleConfirm },
        color: 'gray',
      }}
    >
      <View style={styles.pickerContainer}>
        <PickerSelectedHighlight />
        <View style={StyleSheet.absoluteFill}>
          <ScrollView
            ref={scrollViewRef}
            showsVerticalScrollIndicator={false}
            snapToInterval={ITEM_HEIGHT}
            snapToAlignment="start"
            decelerationRate="fast"
            onScroll={handleScroll}
            scrollEventThrottle={16}
            onContentSizeChange={handleContentSizeChange}
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
          >
            {items.map((item, index) => (
              <PickerItem
                key={item.value ?? 'none'}
                label={item.label}
                index={index}
                isSelected={index === selectedIndex}
                onPress={handleItemPress}
              />
            ))}
          </ScrollView>
        </View>
        <PickerGradientOverlay />
      </View>
    </BottomSheetContainer>
  );
};

export const useSizePickerBottomSheet = createUseBottomSheetModal<
  PickerResult,
  PickerParam
>(SizePickerContent);

const styles = StyleSheet.create({
  pickerContainer: {
    height: PICKER_HEIGHT,
    overflow: 'hidden',
  },
  scrollView: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scrollContent: {
    paddingVertical: ITEM_HEIGHT * 2,
    backgroundColor: 'transparent',
  },
  selectedHighlight: {
    position: 'absolute',
    top: ITEM_HEIGHT * 2,
    left: 12,
    right: 12,
    height: ITEM_HEIGHT,
    borderRadius: 8,
    backgroundColor: Colors.bg_secondary,
  },
});
